import * as Res from "./res";
import { createElement } from "./_dom";
import { registerComponent } from "./component";
import { checkFileTypes } from "./checkFileType";

const isSafari = navigator.userAgent.toLowerCase().indexOf('safari/') !== -1 && navigator.userAgent.toLowerCase().indexOf('version/') !== -1;

function createDeleteMarker(name) {
	return createElement("input", { type: "checkbox", name: name + "_delete", checked: "", hidden: "" });
}

function getChooserTitle() {
	return Res.getString("form.file.choose.1") || "Choose file";
}

function getRemoveTitle() {
	return Res.getString("form.file.remove") || "Remove";
}

function getConfirmTitle() {
	return Res.getString("form.file.confirm") || "Are you sure you want to remove selected file?";
}

function createChooser(clickHandler) {
	const title = getChooserTitle();
	const button = createElement("button", {
		type: "button",
		title,
		class: "v3-filepicker__choose v3-controlgroup__item-last v3-button",
		onclick: clickHandler
	}
	);
	return button;
}

function createDelete(title, clickHandler) {
	const button = createElement("button", {
		type: "button",
		title,
		class: 'v3-filepicker__delete v3-controlgroup__item v3-button ui-icon-remove ui-icon-only',
		onclick: clickHandler
	}, createElement("span", { class: "ui-icontext" }, title));
	return button;
}

const createFileName = (title, href) => (href
	? createElement("a", { href, title, target: "_blank", class: 'v3-filepicker__filename v3-input v3-controlgroup__item-first' }, title)
	: createElement("span", { class: 'v3-filepicker__filename v3-input v3-controlgroup__item-first' }, title)
);


function formatFileSize(size) {
	let precision = 1;
	if (size) {
		var unit;
		if (size < 1024) {
			unit = "b";
			precision = 0;
		} else if (size < 1024 * 1024) {
			unit = "K";
			size /= 1024;
		} else if (size < 1024 * 1024 * 1024) {
			unit = "M";
			size /= 1024 * 1024;
		} else {
			unit = "G";
			size /= 1024 * 1024 * 1024;
		}
		return ` (${size.toFixed(precision).toLocaleString()}${unit})`;
	}
}

class FilePicker {

	constructor(fileInput, options = {}) {
		this.#fileInput = fileInput;
		this.#fileInput.classList.add("v3-filepicker__input-file");
		this.#options = {
			suppressTypeCheck: false,
			labelWrongType: "Selected file type is not valid. Valid types are:",
			...options
		};
		this.#fileInput.addEventListener("change", this.#onSetFileName);
		const wrapper = createElement("span", { class: "v3-filepicker v3-controlgroup focusable" });
		const dropZone = createElement("div", { class: "v3-filepicker__drop-zone" }, "Drop files(s) here");
		this.#fileInput.before(wrapper);
		this.#chooser = createChooser(this.#forwardClick);
		wrapper.append(dropZone, this.#fileInput, this.#chooser);
		const { filename: name, fileurl: url, allowremove: allowRemove } = fileInput.dataset;
		this.setFileName({ name, url, nfiles: 1, allowRemove });
		this.#setupDropTarget(wrapper);
	}

	#forwardClick = () => {
		this.#fileInput.click();
	};

	#setupDropTarget = element => {
		element.addEventListener("dragenter", e => {
			// do not .preventDefault() as it breaks dropping into input[type=file]
			e.currentTarget.classList.add("v3-filepicker--dragover");
		});
		element.addEventListener("dragover", e => {
			e.preventDefault();
			// Safari <= 26.4 does not set relatedTarget in dragleave, so we emulate dragleave by checking when dragover event stream is stopped 
			if (isSafari) {
				const currentTarget = e.currentTarget;
				clearTimeout(this.#timerId);
				this.#timerId = setTimeout(() => currentTarget.classList.remove("v3-filepicker--dragover"), 300);
			}
		});
		element.addEventListener("dragleave", e => {
			if (!isSafari) {
				const { currentTarget, relatedTarget } = e;
				if (!currentTarget.contains(relatedTarget))
					currentTarget.classList.remove("v3-filepicker--dragover");
			}
		});
		element.addEventListener("drop", ({ currentTarget }) => {
			currentTarget.classList.remove("v3-filepicker--dragover");
			if (isSafari && this.#timerId) {
				clearTimeout(this.#timerId);
				this.#timerId = undefined;
			}
		});
	};

	#triggerChange = () => {
		this.#fileInput.dispatchEvent(new Event("change", { bubbles: true }));
	};

	#removeSelectedFile = () => {
		this.#fileInput.value = "";
		if(this.#fileWasPreselected) {
			this.#fileWasPreselected = false;
			this.#fileInput.after(createDeleteMarker(this.#fileInput.name));
		}
		this.clearFileName();
		this.#triggerChange();
	};

	#removePreSelectedFile = () => {
		if (!confirm(getConfirmTitle()))
			return;
		this.#fileWasPreselected = false;
		this.#fileInput.value = "";
		this.#fileInput.after(createDeleteMarker(this.#fileInput.name));
		this.clearFileName();
		this.#triggerChange();
	};

	#setupChooser = (hasFile) => {
		const title = getChooserTitle();
		this.#chooser.classList[hasFile ? "add" : "remove"]("ui-icon-pick-file", "ui-icon-only", "v3-controlgroup__item-last");
		this.#chooser.replaceChildren(
			hasFile ?
				createElement("span", { class: "ui-icontext" }, title) :
				createElement("div", { class: "v3-filepicker__placeholder-container" },
					createElement("span", { class: "v3-filepicker__placeholder" }, getChooserTitle()),
					createElement("i", { class: "ui-icon-pick-file ui-icon-only" }))
		);
	};

	/* true if reported invalid*/
	#checkFileTypes = (event) => {
		const me = this.#fileInput;
		if (me.files.length > 0 && me.accept && !this.#options.suppressTypeCheck) {
			// file is bad file or null
			var invalidFile = checkFileTypes(me);
			if (invalidFile) {
				event.stopPropagation();
				event.preventDefault();
				alert(me.dataset.message || me.title || (this.#options.labelWrongType + "\r\n\r\n" + me.accept));
				return true;
			}
		}
	};

	#onSetFileName = (event) => {
		if (this.#fileInput.value && !this.#checkFileTypes(event)) {
			let nfiles = 1;
			if (this.#fileInput.files && (nfiles = this.#fileInput.files.length) > 1) {
				this.setFileName({ name: `${nfiles} files`, nfiles });
			} else {
				var size = 0;
				try { size = this.#fileInput.files[0].size; } catch (e) { /**/ }
				var name = this.#fileInput.value;
				var n = Math.max(name.lastIndexOf("\\"), name.lastIndexOf("/"));
				if (n >= 0) name = name.substring(n + 1);
				this.setFileName({ name, nfiles, size });
			}
		} else {
			this.clearFileName();
		}
	};

	clearFileName = () => {
		this.setFileName({ name: "", nfiles: 0 });
	};

	setFileName = (options) => {
		const { name, nfiles, url, allowRemove, size } = {
			name: "",
			nfiles: 0,
			size: 0,
			allowRemove: true,
			...options
		};

		const parent = this.#fileInput.parentNode;
		parent.querySelectorAll(".v3-filepicker__filename, .v3-filepicker__delete").forEach(item => item.remove());

		if (name) {
			const label = createFileName(name, url);
			let remover, deleteLabel;
			if (allowRemove === "") {
				this.#fileWasPreselected = true;
				remover = this.#removePreSelectedFile;
				deleteLabel = getRemoveTitle();
			} else if (allowRemove === true) {
				remover = this.#removeSelectedFile;
				// deleteLabel = Res.getString("form.file.undo");
				deleteLabel = getRemoveTitle();
			} else {
				remover = null;
			}
			if (nfiles === 1) {
				var ext = name.lastIndexOf(".");
				if (ext >= 0) ext = name.substring(ext + 1).toLowerCase();
				label.classList.add("fileicon", "ext" + ext);
				const sizeString = formatFileSize(size);
				if (sizeString)
					label.append(sizeString);
			}
			this.#fileInput.after(label);
			if (remover) {
				const deleteButton = createDelete(deleteLabel, remover);
				label.after(deleteButton);
			}
		}
		this.#setupChooser(!!name);
	};

	#fileInput;
	#fileWasPreselected;
	#chooser;
	#options;
	#timerId;

	static create(fileInput) {
		return new FilePicker(fileInput);
	}
}

const filepicker = registerComponent(FilePicker.create);

function init(context, selector = "input[type=file]:not([data-signature])") {
	return filepicker(selector, context);
}

export { filepicker, init };
